import {
  Box,
  Button,
  Col,
  Grid,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  NumberInput,
  Textarea,
  Radio
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import { notifications } from 'notifications';
import { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLoginUser } from 'redux/selectors/useLoginUser';
import { API } from 'services';
import { GradientButton } from 'components/GradientButton';

export function TimesheetGenerate() {
  const navigate = useNavigate();
  const { loginUser } = useLoginUser();
  const [searchParams] = useSearchParams();
  const [isLoading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [entryType, setEntryType] = useState<string>('');

  const timesheetId = searchParams.get('timesheet_id');

  const isEditMode = true;

  const form = useForm({
    initialValues: {
      date: new Date(),
      sign_in_coa: '',
      signed_by_ts_sign_in: '',
      course_name: '',
      course_run_id: '',
      no_of_assessment: 0,
      sign_out_coa: '',
      signed_by_ts_sign_out: '',
      no_of_hours_worked: 0,
      remarks: ''
    },
    validate: (values) => {
      const errors: any = {};
      if (entryType === 'in') {
        if (!values.sign_in_coa?.trim()) errors.sign_in_coa = 'Please enter Sign In COA';
      } else if (entryType === 'out') {
        if (!values.sign_out_coa?.trim()) errors.sign_out_coa = 'Please enter Sign Out COA';
      } else {
        errors.entryType = 'Please select Entry Type';
      }
      if (!values.course_name?.trim()) errors.course_name = 'Please select Course Name';
      if (!values.course_run_id?.trim()) errors.course_run_id = 'Please select Course Run';
      return errors;
    },
  });

  // Fetch available courses for dropdown
  const coursesQuery = useQuery(['courses'], () => {
    return API.getCourseByCoAssessor().then((res: any) => res.data);
  });

  // Fetch course runs when course is selected
  const courseRunsQuery = useQuery(
    ['courseRuns', form.values.course_name, selectedDate],
    async () => {
      if (!form.values.course_name) return [];
      // Calculate 2 months before start and selected month end
      const prevMonth = new Date(selectedDate);
      prevMonth.setMonth(prevMonth.getMonth() - 2);
      const startOfPrevMonth = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1);
      const endOfSelectedMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
      const dateStart = startOfPrevMonth.toISOString().split('T')[0];
      const dateEnd = endOfSelectedMonth.toISOString().split('T')[0];
      const runs = await API.getCourseRun({
        course_id: form.values.course_name,
        date_start: dateStart,
        date_end: dateEnd
      }).then((res: any) => res.data);
      // If editing and trainertimesheet_courserun_id exists, auto-select it if present
      if (isEditMode && timesheetQuery?.data?.TrainerTimesheet?.trainertimesheet_courserun_id) {
        const matchId = timesheetQuery.data.TrainerTimesheet.trainertimesheet_courserun_id?.toString();
        if (runs.some((r: any) => r.courserun_id?.toString() === matchId)) {
          form.setFieldValue('course_run_id', matchId);
        }
      }
      return runs;
    },
    {
      enabled: !!form.values.course_name,
    }
  );



  let timesheetQuery: any = null;

  if (timesheetId) {
    timesheetQuery = useQuery(
      ['timesheet', timesheetId],
      () => API.getTimesheetDetails({ timesheet_id: timesheetId! }).then((res) => res.data),
      {
        enabled: isEditMode,
      }
    );
  }

 let canEdit = true;
  if(timesheetQuery){
    if(timesheetQuery.data){
      if((timesheetQuery.data?.TrainerTimesheet?.trainertimesheet_status === 'Rejected')  || (timesheetQuery.data?.TrainerTimesheet?.trainertimesheet_sign_out_coa == null)){
        canEdit = true;
      }else{
        canEdit = false;
      }
      
    }
  }


  // Clear course run when course changes
  useEffect(() => {
    if (form.values.course_name) {
      form.setFieldValue('course_run_id', '');
    }
  }, [form.values.course_name]);


  useEffect(() => {

    if (courseRunsQuery.data && courseRunsQuery.data.length > 0 && !form.values.course_run_id) {
      form.setFieldValue('course_run_id', courseRunsQuery.data[0].courserun_id?.toString() || '');
    }
  }, [courseRunsQuery.data]);

  // Auto select 'in' for new entries
  useEffect(() => {
    if (!timesheetId) {
      setEntryType('in');
      const now = new Date();
      const formatted = now.toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      form.setFieldValue('sign_in_coa', formatted);
    }
  }, [timesheetId]);

  // Populate form with existing data when editing
  if(timesheetQuery){
    useEffect(() => {
      if (isEditMode && timesheetQuery.data?.data) {
        const timesheetData = timesheetQuery.data.data;
        form.setValues({
          date: new Date(timesheetData.date),
          sign_in_coa: timesheetData.sign_in_coa || '',
          signed_by_ts_sign_in: timesheetData.signed_by_ts_sign_in || '',
          course_name: timesheetData.course_name || '',
          course_run_id: timesheetData.course_run_id || '',
          no_of_assessment: timesheetData.no_of_assessment || 0,
          sign_out_coa: timesheetData.sign_out_coa || '',
          signed_by_ts_sign_out: timesheetData.signed_by_ts_sign_out || '',
          no_of_hours_worked: timesheetData.no_of_hours_worked || 0,
          remarks: timesheetData.remarks || ''
        });
        setSelectedDate(new Date(timesheetData.date));
      }
    }, [isEditMode, timesheetQuery.data, form]);

  // Populate form with existing data when editing
    useEffect(() => {
      if (isEditMode && timesheetQuery.data?.TrainerTimesheet && coursesQuery.data) {
        const timesheetData = timesheetQuery.data.TrainerTimesheet;
        setEntryType('out'); // Auto select out for editing
        form.setValues({
          date: new Date(timesheetData.trainertimesheet_date),
          sign_in_coa: timesheetData.trainertimesheet_sign_in_coa || '',
          signed_by_ts_sign_in: timesheetData.trainertimesheet_signed_by_ts_sign_in || '',
          course_name: timesheetData.trainertimesheet_course_id?.toString() || '',
          course_run_id: timesheetData.trainertimesheet_courserun_id || '',
          no_of_assessment: timesheetData.trainertimesheet_assessment_count || 0,
          sign_out_coa: timesheetData.trainertimesheet_sign_out_coa || '',
          signed_by_ts_sign_out: timesheetData.trainertimesheet_signed_by_ts_sign_out || '',
          no_of_hours_worked: parseFloat(timesheetData.trainertimesheet_working_hrs) || 0,
          remarks: timesheetData.trainertimesheet_remark || ''
        });
        setSelectedDate(new Date(timesheetData.trainertimesheet_date));

        // If sign_out_coa is not null, auto-fill with current datetime
        if (!timesheetData.trainertimesheet_sign_out_coa) {
          const now = new Date();
          const formatted = now.toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
          form.setFieldValue('sign_out_coa', formatted);
        }
      }
    }, [isEditMode, timesheetQuery.data, coursesQuery.data]);

  }

  



  // Handle loading and error states
  if (coursesQuery.isLoading) {
    return (
      <Box p="md">
        <Title order={2} mb="lg">Timesheet </Title>
        <Text>Loading...</Text>
      </Box>
    );
  }

  if (coursesQuery.isError ) {
    return (
      <Box p="md">
        <Title order={2} mb="lg">Timesheet</Title>
        <Text color="red">Error loading data. Please try again.</Text>
      </Box>
    );
  }

  const courseOptions = coursesQuery.data?.map((course: any) => ({
    value: course.value.toString(),
    label: course.label
  })) || [];

  const courseRunOptions = courseRunsQuery.data?.map((courseRun: any) => {
    const fromDate = courseRun.courserun_course_from ? new Date(courseRun.courserun_course_from).toLocaleDateString('en-GB') : '';
    const toDate = courseRun.courserun_course_to ? new Date(courseRun.courserun_course_to).toLocaleDateString('en-GB') : '';
    const label = fromDate && toDate ? `${fromDate} - ${toDate}` : courseRun.courserun_reference_number || courseRun.courserun_id?.toString() || '';

    return {
      value: courseRun.courserun_id?.toString() || '',
      label: label
    };
  }) || [];


  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const timesheetData = {
        ...values,
        date: selectedDate.toISOString().split('T')[0], // Format date as YYYY-MM-DD
        trainer_id: loginUser.id
      };

      let response;
      if (timesheetId) {
        response = await API.updateTimesheet({
          ...timesheetData,
          timesheet_id: timesheetId
        });
      } else {
        response = await API.submitTimesheet(timesheetData);
      }
      
      if (response.data.status) {
        notifications.success({
          title: isEditMode ? 'Timesheet updated successfully' : 'Timesheet submitted successfully',
          message: '',
        });
        navigate('/timesheets');
      } else {
        notifications.error({
          title: 'Operation failed',
          message: response.data.message || 'Please try again',
        });
      }
    } catch (error) {
      notifications.error({
        title: 'Operation failed',
        message: 'Please try again',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p="md">
      <Title order={2} mb="lg">
        Timesheet
      </Title>
      
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing="md">
          {/* Date */}
          <Grid>
            <Col span={6}>
              <Text weight={500} size="sm" mb="xs">Date</Text>
              <DatePicker
                selected={selectedDate}
                onChange={(date) => canEdit && setSelectedDate(date || new Date())}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                wrapperClassName="w-100"
                readOnly={!canEdit}
              />
            </Col>
          </Grid>

          {/* Entry Type */}
          <Grid>
            <Col span={12}>
              <Text weight={500} size="sm" mb="xs">Entry Type</Text>
              <Radio.Group
                value={entryType}
                onChange={(value) => {
                  if (canEdit) {
                    const newType = value as string;
                    setEntryType(newType);
                    if (!timesheetId && newType === 'in') {
                      const now = new Date();
                      const formatted = now.toLocaleString('en-GB', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      });
                      form.setFieldValue('sign_in_coa', formatted);
                    }
                    // For editing, 'out' is auto selected, no need to fill
                    // For new 'out', don't auto fill
                  }
                }}
              >
                <Group mt="xs">
                  <Radio value="in" label="In" disabled={!canEdit || !!timesheetId} />
                  <Radio value="out" label="Out" disabled={!canEdit || !timesheetId} />
                </Group>
              </Radio.Group>
            </Col>
          </Grid>

          {/* Sign In Section */}
          <Grid>
            <Col span={6}>
              <TextInput
                label="Sign In (COA)"
                placeholder="Enter sign in COA"
                readOnly
                {...form.getInputProps('sign_in_coa')}
              />
            </Col>
            <Col span={6}>
              <TextInput
                label="Sign Out (COA)"
                placeholder="Enter sign out COA"
                readOnly
                {...form.getInputProps('sign_out_coa')}
              />
            </Col>
          </Grid>

          {/* Course Information */}
          <Grid>
            <Col span={6}>
              <Select
                label="Course Name"
                placeholder="Select course"
                data={courseOptions}
                readOnly={!canEdit}
                {...form.getInputProps('course_name')}
              />
            </Col>
            <Col span={6}>
              <Select
                label="Course Run"
                placeholder="Select course run"
                data={courseRunOptions}
                readOnly={!canEdit}
                disabled={!form.values.course_name || courseRunsQuery.isLoading}
                {...form.getInputProps('course_run_id')}
              />
            </Col>
          </Grid>

          {/* Assessment Count */}
          <Grid>
            <Col span={6}>
              <NumberInput
                label="No of Assessment"
                placeholder="Enter number of assessments"
                min={0}
                readOnly={!canEdit}
                {...form.getInputProps('no_of_assessment')}
              />
            </Col>
            <Col span={6}>
              <NumberInput
                label="No of Hours Worked"
                placeholder="Enter hours worked"
                min={0}
                precision={1}
                step={0.5}
                readOnly={!canEdit}
                {...form.getInputProps('no_of_hours_worked')}
              />
            </Col>
          </Grid>



          {/* Remarks */}
          <Grid>
            <Col span={12}>
              <Textarea
                label="Remarks"
                placeholder="Enter any additional remarks"
                readOnly={!canEdit}
                {...form.getInputProps('remarks')}
              />
            </Col>
          </Grid>

          {/* Submit Button */}
          <Group position="right" mt="xl">
            <Button variant="outline" onClick={() => navigate('/timesheets')}>
              Cancel
            </Button>
            {canEdit && (
              <GradientButton type="submit" loading={isLoading}>
                Submit Timesheet
              </GradientButton>
            )}
   
          </Group>
        </Stack>
      </form>
    </Box>
  );
}